import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { Constants, overdraftRulesError, overdraftRulesErrorTypes, trustIncomePrincipalErrorMap } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { percentFormatter } from '../../../shared/utilities/PercentFormatter';
import { percentAllowedChars } from '../../../shared/utilities/regex-patterns';
import { PtrsInfo } from '../../models';
import { AchTransactionDetailsForm, CheckTransactionDetailsForm, ItTransactionDetailsForm, WireTransactionDetailsForm } from '../../models/form/TransactionFormGroup';

@Component({
  standalone: false,
  selector: 'mm-trust-income-principal',
  templateUrl: './trust-income-principal.component.html',
  styleUrls: ['./trust-income-principal.component.scss']
})
export class TrustIncomePrincipalComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm | WireTransactionDetailsForm | ItTransactionDetailsForm | AchTransactionDetailsForm>;
  @Input() availableCash: number;
  @Input() datePickerControlName: string;
  @Input() overdraftRulesFutureDate: Date;
  @Input() incomePercent$: Observable<string>;
  @Input() principalPercent$: Observable<string>;
  @Input() ptrsInfo$: Observable<PtrsInfo>;
  @Input() component: string;
  @Input() shouldStyleHorizontal: boolean = false;
  @Input() isFromAccount: boolean;
  @Input() isEdit: boolean = false;
  trustIncomeControlName: 'trustIncomePercent' | 'trustIncomeToPercent';
  trustPrincipalControlName: 'trustPrincipalPercent' | 'trustPrincipalToPercent';
  percentAllowedChars = percentAllowedChars;
  trustIncomePercentToolTip: string = Constants.trustIncomePercentToolTip;
  trustPrincipalPercentToolTip: string = Constants.trustPrincipalPercentToolTip;
  principalPercentOnly: string = Constants.principalPercentageOnly;
  trustIncomePercentValue: string = '';
  trustPrincipalPercentValue: string = '';
  piCode: string = 'B';
  ptrsInfo: PtrsInfo;
  isWireTransaction: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder, public validationService: ValidateInputService) { }

  ngOnInit(): void {
    this.trustIncomeControlName = this.isFromAccount ? 'trustIncomePercent' : 'trustIncomeToPercent';
    this.trustPrincipalControlName = this.isFromAccount ? 'trustPrincipalPercent' : 'trustPrincipalToPercent';

    // Subscribe to income and principal percent observables if they exist
    if (this.incomePercent$ !== undefined) {
      this.incomePercent$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(trustIncomePercentValue => {
        this.trustIncomePercentValue = trustIncomePercentValue;
        // Sets the value to the default unless in edit mode where we want to keep the existing value
        if (this.parentForm?.contains(this.trustIncomeControlName) && !this.isEdit) {
          this.trustIncomePercent?.setValue(percentFormatter(this.trustIncomePercentValue), { emitEvent: false });
        }
      });
    }
    if (this.principalPercent$ !== undefined) {
      this.principalPercent$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(trustPrincipalPercentValue => {
        this.trustPrincipalPercentValue = trustPrincipalPercentValue;
        // Sets the value to the default unless in edit mode where we want to keep the existing value
        if (this.parentForm?.contains(this.trustPrincipalControlName) && !this.isEdit) {
          this.trustPrincipalPercent?.setValue(percentFormatter(this.trustPrincipalPercentValue), { emitEvent: false });
        }
      });
    }

    this.ptrsInfo$.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.ptrsInfo = info;
      if (!info.overdraftRules.notFound) {
        this.piCode = info.overdraftRules.piCode;
      }
    });

    // Sets form controls unless in edit mode where we want to keep the existing form control and values
    if (!this.isEdit) {
      this.parentForm.setControl(this.trustIncomeControlName, this.fb.nonNullable.control(this.trustIncomePercentFormatted));
      this.parentForm.setControl(this.trustPrincipalControlName, this.fb.nonNullable.control(this.trustPrincipalPercentFormatted));
    }
    
    this.parentForm.get(this.trustIncomeControlName)?.addValidators(
      [Validators.required,
      CustomValidators.zeroOrGreater,
      CustomValidators.lessThanOrEqualTo100]
    );
    this.parentForm.get(this.trustPrincipalControlName)?.addValidators(
      [Validators.required,
      CustomValidators.zeroOrGreater,
      CustomValidators.lessThanOrEqualTo100]
    );
    this.parentForm.addValidators(CustomValidators.sumTo100(this.trustIncomeControlName, this.trustPrincipalControlName, this.component));
    if (!this.ptrsInfoCallHasError && this.component === 'source') {
      this.parentForm.addValidators(CustomValidators.OverdraftRulesValidator(this.ptrsInfo, this.availableCash, this.datePickerControlName, this.overdraftRulesFutureDate, this.trustIncomeControlName, this.trustPrincipalControlName));
    }

    this.checkDefaultOverdraftRules();

    if (this.parentForm?.parent?.value?.transactionType === 'wire') {
      this.isWireTransaction = true;
    }
  }

  clearIncomePercent(): void {
    this.trustIncomePercent?.reset('0%');
    this.trustIncomePercent?.markAsTouched();
  }

  clearPrincipalPercent(): void {
    this.trustPrincipalPercent?.reset('0%');
    this.trustPrincipalPercent?.markAsTouched();
  }

  checkDefaultOverdraftRules(): void {
    if (this.piCode === 'P') {
      this.trustIncomePercent?.setValue('0%');
      this.trustPrincipalPercent?.setValue('100%');
      this.trustIncomePercent?.disable();
      this.trustPrincipalPercent?.disable();
    }
  }

  get ptrsInfoCallHasError(): boolean {
    return this.ptrsInfo?.hasError ||
      this.ptrsInfo.balances?.hasError ||
      this.ptrsInfo.overdraftRules?.hasError ||
      this.ptrsInfo.balances?.notFound ||
      this.ptrsInfo.overdraftRules?.notFound;
  }

  get trustIncomePercent(): FormControl<string> | undefined {
    return this.parentForm.get(this.trustIncomeControlName) as FormControl<string>;
  }

  get trustPrincipalPercent(): FormControl<string> {
    return this.parentForm.get(this.trustPrincipalControlName) as FormControl<string>;
  }

  get trustIncomePercentFormatted(): string {
    return percentFormatter(this.trustIncomePercentValue) ?? '';
  }

  get trustPrincipalPercentFormatted(): string {
    return percentFormatter(this.trustPrincipalPercentValue) ?? '';
  }

  get trustIncomeAndPrincipalError(): string {
    if ((this.trustPrincipalPercent && this.trustPrincipalPercent?.errors) || (this.trustIncomePercent && this.trustIncomePercent?.errors)) {
      const errorKeys: string[] = (Object.keys(this.trustPrincipalPercent?.errors ?? {})).concat(Object.keys(this.trustIncomePercent?.errors ?? {}));
      if (errorKeys.length > 0) {
        if (this.trustPrincipalPercent?.value !== '' && this.trustIncomePercent?.value !== '') {
          return `Income & Principal percentage ${trustIncomePrincipalErrorMap['greaterThan100' as keyof typeof trustIncomePrincipalErrorMap]}`;
        }
        return `Income & Principal percentage ${trustIncomePrincipalErrorMap['required' as keyof typeof trustIncomePrincipalErrorMap]}`;
      }
    }
    return '';
  }

  get trustIncomeAndPrincipalSumError(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(this.parentForm.errors ?? {});
      var component = this.component;
      if (errorKeys.length > 0) {
        var sumError = errorKeys.find(function (item) {
          return item.includes(component);
        });
        return trustIncomePrincipalErrorMap[sumError as keyof typeof trustIncomePrincipalErrorMap];
      }
    }
    return '';
  }

  get hasTrustIncomeAndPercentSumError(): boolean {
    if (this.parentForm && this.parentForm.errors) {
      if (this.component == 'source') {
        return this.parentForm.errors['sourceDoNotSumTo100'] ?? false;
      } else if (this.component == 'receiving') {
        return this.parentForm.errors['receivingDoNotSumTo100'] ?? false;
      }
    }
    return false;
  }

  get isTrustIncomePercentInvalid(): boolean {
    return (this.trustIncomePercent?.invalid && this.trustIncomePercent?.touched) ||
      this.hasTrustIncomeAndPercentSumError ||
      this.hasIncomeOverdraftError ||
      this.hasIncomeAndPrincipalOverdraftError;
  }

  get isTrustPrincipalPercentInvalid(): boolean {
    return (this.trustPrincipalPercent?.invalid && this.trustIncomePercent?.touched) ||
      this.hasTrustIncomeAndPercentSumError ||
      this.hasPrincipalOverdraftError ||
      this.hasIncomeAndPrincipalOverdraftError;
  }

  get overdraftRulesError(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(this.parentForm.errors ?? {});
      return errorKeys.length > 0 ? overdraftRulesError[errorKeys.find((item) => {
        return item.toLowerCase().includes('percenterror') || item.toLowerCase().includes('principalpercenterror');
      }) as overdraftRulesErrorTypes] : '';
    }
    return '';
  }

  get hasIncomeOverdraftError(): boolean {
    if (this.parentForm && this.parentForm.errors) {
      let errorKey: overdraftRulesErrorTypes = 'incomePercentError';
      return this.parentForm.errors[errorKey] ?? false;
    }
    return false;
  }

  get hasPrincipalOverdraftError(): boolean {
    if (this.parentForm && this.parentForm.errors) {
      let errorKey: overdraftRulesErrorTypes = 'principalPercentError';
      return this.parentForm.errors[errorKey] ?? false;
    }
    return false;
  }

  get hasIncomeAndPrincipalOverdraftError(): boolean {
    if (this.parentForm && this.parentForm.errors) {
      let errorKey: overdraftRulesErrorTypes = 'incomeAndPrincipalPercentError';
      return this.parentForm.errors[errorKey] ?? false;
    }
    return false;
  }

  formatIncomePercent(): void {
    if (this.trustIncomePercent && this.trustIncomePercent?.valid) {
      let temp = this.trustIncomePercent?.value.replace(/[^0-9.]/g, '');
      this.trustIncomePercent?.setValue(percentFormatter(temp));
    }
  }

  formatPrincipalPercent(): void {
    if (this.trustPrincipalPercent && this.trustPrincipalPercent?.valid) {
      let temp = this.trustPrincipalPercent?.value.replace(/[^0-9.]/g, '');
      this.trustPrincipalPercent?.setValue(percentFormatter(temp));
    }
  }

  removeExtraDecimalPlaceFromIncome(): void {
    if (this.trustIncomePercent) {
      let temp = this.trustIncomePercent?.value;
      temp = (temp.indexOf(".") >= 0) ? (temp.substr(0, temp.indexOf(".")) + temp.substr(temp.indexOf("."), 3)) : temp;
      this.trustIncomePercent?.setValue(temp);
    }
  }

  removeExtraDecimalPlaceFromPrincipal(): void {
    if (this.trustPrincipalPercent) {
      let temp = this.trustPrincipalPercent?.value;
      temp = (temp.indexOf(".") >= 0) ? (temp.substr(0, temp.indexOf(".")) + temp.substr(temp.indexOf("."), 3)) : temp;
      this.trustPrincipalPercent?.setValue(temp);
    }
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl(this.trustIncomeControlName);
    this.parentForm.removeControl(this.trustPrincipalControlName);
    this.parentForm.setValidators(null);

    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
